/**
 * Prosa de fornecedor (`description` do hotel e do detalhe do quarto) chega
 * com marcação — `<p><strong>1 cama de casal</strong></p><p>33 metros
 * quadrados</p><br/>…` — e toda superfície nossa a renderiza como TEXTO
 * (nó de texto React, `whitespace-pre-line`), então as tags apareciam para o
 * viajante (#2758). A saída aqui é texto puro: nada volta como HTML, e nenhum
 * consumidor precisa de `dangerouslySetInnerHTML` para exibir conteúdo de
 * terceiro.
 *
 * Não é um parser de HTML: é o mínimo para o conteúdo virar linhas legíveis.
 * Todas as regexes são lineares (sem backtracking super-linear, S5852).
 *
 * CONTRATO: aplique UMA VEZ, na LEITURA (as views deste pacote) — é lá que os
 * blocos já persistidos com marcação também são cobertos. A conversão é lossy e
 * NÃO é idempotente: reaplicá-la à própria saída decodifica de novo o `&lt;`
 * literal que a primeira passada restaurou. Quem grava o dado (modal-session)
 * guarda a prosa crua; ver o teste `is single-pass`.
 */

/** Fim de bloco vira quebra de linha; o resto das tags some sem deixar espaço,
 *  para `Frigo<em>bar</em>` não virar `Frigo bar`. */
const BLOCK_END = /<\/(?:p|div|li|tr|h[1-6]|section|article|blockquote)\s*>/gi;
const LINE_BREAK = /<br\s*\/?>/gi;

/** O mesmo que `.replaceAll(/<[^>]*>/g, '')`, por `indexOf`: a regex reescaneava
 *  até o fim a partir de CADA `<` sem `>` depois (Sonar S5852, quadrático). Sem
 *  `>` depois de um `<`, nenhum `<` seguinte fecha — o resto fica como está. */
function stripTags(text: string): string {
  let kept = '';
  let cursor = 0;
  for (;;) {
    const open = text.indexOf('<', cursor);
    const close = open === -1 ? -1 : text.indexOf('>', open + 1);
    if (close === -1) return kept + text.slice(cursor);
    kept += text.slice(cursor, open);
    cursor = close + 1;
  }
}

/** Uma passada só: decodificar em cascata transformaria `&amp;lt;` (um `&lt;`
 *  literal escrito pelo fornecedor) num `<`. As numéricas cobrem os acentos,
 *  que é como o feed costuma escapá-los (`Caf&#233;`); as nomeadas ficam nas
 *  poucas de estrutura — uma tabela de `&eacute;` & cia. seria um parser. */
const ENTITY = /&(?:amp|lt|gt|quot|apos|nbsp|#\d{1,7}|#x[\da-f]{1,6});/gi;
const NAMED_TEXT: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&nbsp;': ' ',
};

function decodeEntity(entity: string): string {
  const lower = entity.toLowerCase();
  const named = NAMED_TEXT[lower];
  if (named !== undefined) return named;
  const digits = lower.slice(lower.startsWith('&#x') ? 3 : 2, -1);
  const code = Number.parseInt(digits, lower.startsWith('&#x') ? 16 : 10);
  /* Fora do plano Unicode válido volta como veio: melhor a entidade crua na
   *  tela do que um `String.fromCodePoint` que estoura. */
  return code > 0 && code <= 0x10_ff_ff ? String.fromCodePoint(code) : entity;
}

/**
 * Converte marcação de fornecedor em texto puro em linhas. `undefined` quando
 * não sobra conteúdo — o campo é opcional em todo lugar, e string vazia
 * renderizaria um parágrafo em branco.
 */
export function htmlToPlainText(input: string | null | undefined): string | undefined {
  if (!input) return undefined;
  const text = stripTags(input.replaceAll(LINE_BREAK, '\n').replaceAll(BLOCK_END, '\n'))
    .replaceAll(ENTITY, decodeEntity)
    .replaceAll(/[^\S\n]+/g, ' ')
    .replaceAll(/ ?\n ?/g, '\n')
    .replaceAll(/\n{3,}/g, '\n\n')
    .trim();
  return text || undefined;
}
